<template>
  <div class="score-graph-wrapper">
    <div
      id="analytics-chart"
      class="analytics-chart"
      :class="{'hidden': !showGraph}"
      >
      <div class="chart-wrapper">
        <ScoreHistoryChart
          class="score-history-chart"
          v-if="!showLoading && storeList.length != 0 && graphData.length != 0 "
          :scores="graphData"
          :stores="storeList"
          :scoreType="dataConfig.scoreType"
          :scoreTypeMonths="dataConfig.scoreTypeMonths"
          :blnCurrentMonth="dataConfig.blnCurrentMonth"
        />
        <div class="d-flex justify-center" v-if="showLoading">
          <sb-loading-icon />
        </div>
      </div>
    </div>

    <!-- Report Options Section -->
    <div class="d-flex analytics-options mb-2">
      <div class="d-flex mr-4">
        <h4 class="mr-1">grid size: </h4>
        <button 
          class="action-button button grid-size" 
          @click="showGraph = !showGraph">
          <fai :icon="['fas', 'expand-alt']" />
        </button>
      </div>
      <div class="d-flex mr-4">
        <h4 class="mr-1">score type: </h4>
        <select
          class="form-control input"
          @input="
            event => {
              filterHandler('scoreType', event.target.value);
            }
          "
        >
          <option
            v-for="(option, index) in scoreTypeList"
            :selected="option.value == dataConfig.scoreType"
            :value="option.value"
            :key="index"
          >
            {{ option.name }}
          </option>
        </select>
      </div>
      <div class="d-flex mr-4">
        <h4 class="mr-1">months: </h4>
        <select
          class="form-control input"
          @input="
            event => {
              filterHandler('scoreTypeMonths', parseInt(event.target.value));
            }
          "
        >
          <option
            v-for="(option, index) in monthList"
            :selected="option.value == dataConfig.scoreTypeMonths"
            :value="option.value"
            :key="index"
          >
            {{ option.name }}
          </option>
        </select>
      </div>
      <div class="d-flex">
        <input
          v-model="dataConfig.blnCurrentMonth"
          id="include-current-check"
          type="checkbox"
          @change="reportRan ? onRunReport() : ''">
        <h4 class="mr-1">include current month</h4>
      </div>
      <button class="action-button button" @click="onRunReport()">run report</button>
    </div>

    <!-- Bottom Section -->
    <div 
      id="analytics-stores" 
      class="analytics-stores"
      :class="{'minimized': showGraph}"> 

      <!-- Selected Store List -->
      <div class="analytics-stores-selected">
        <div class="d-flex">
          <h3 class="mr-2">selected stores</h3>
          <h4>({{selectedStoreList.names.length}} of 10)</h4>
          <div  v-if="selectedStoreList.names.length != 0" class="button-wrapper">
            <button class="action-button button" @click="setSelected('none')">all <fai :icon="['fas', 'times-circle']" /></button>
          </div>
        </div>
        <div v-if="selectedStoreList.names.length != 0" class="selected-store-list">
          <ul>
            <li 
              v-for="(storeName, index) in selectedStoreList.names"
              :key="index"
              class="d-flex justify-space-between">
              <div class="store-name">
                {{ storeName.toLowerCase() }}
              </div>
              <div class="button-wrapper">
                <button 
                  class="action-button button" 
                  @click="removeSelectedStore(storeName)">
                  <fai :icon="['fas', 'times-circle']" />
                </button>
              </div>
            </li>
          </ul>
        </div>
        <div v-else class="selected-store-list d-flex align-center">
          <div class="text-center">
            <p><b>no stores selected</b><br>please use table to the right to find and include stores in your query</p>
          </div>
        </div>
      </div>

      <!-- Store Table -->
      <div class="analytics-stores-list">
        <table class="table-wrapper">
          <thead class="table-header">
            <tr>
              <th class="text-left on-hover button" v-on:mousedown="setSelected('all')">
                select
              </th>
              <th class="text-left">name</th>
              <th class="text-left">store email</th>
            </tr>
          </thead>
          <tbody class="table-body">
            <tr 
              v-for="(row, index) in storeList"
              class="on-hover"
              :class="{'bg-info': selectedStoreList.ids.includes(row.id)}"
              :key="index"
              :ref="row.id"
              v-on:mouseover="onStoreHover(row, 'enter')"
              v-on:mouseleave="onStoreHover(row, 'leave')"
              v-on:mousedown="setSelected(row)"
              >
              <td class="text-center">
                <fai 
                  :icon="['fas', 'check']"
                  v-if="selectedStoreList.ids.includes(row.id)" />
              </td>
              <td class="text-left">
                {{ row.name.toLowerCase() }}
              </td>
              <td class="text-left">
                {{ row.store_email.toLowerCase() }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { feedbackAPI } from "../../../../services/api/feedbackAPI"
import ScoreHistoryChart from '../../../Graphs/ScoreHistoryChart'

export default {
  name: "ScoresGraphContainer",
  components: {
    ScoreHistoryChart
  },
  data() {
    return {
      scoreTypeList: [
        {
          name: "nps scores",
          value: "nps"
        },
        {
          name: "cs scores",
          value: "crs"
        },
        {
          name: "product scores",
          value: "product"
        }
      ],
      monthList: [
        {
          name: "2 months",
          value: 2
        },
        {
          name: "3 months",
          value: 3
        },
        {
          name: "4 months",
          value: 4
        },
        {
          name: "5 months",
          value: 5
        },
        {
          name: "6 months",
          value: 6
        },
        {
          name: "7 months",
          value: 7
        },
        {
          name: "8 months",
          value: 8
        },
        {
          name: "9 months",
          value: 9
        },
        {
          name: "10 months",
          value: 10
        },
        {
          name: "11 months",
          value: 11
        },
        {
          name: "12 months",
          value: 12
        },
      ],
      analyticsStoresMaximized: false,
      storeList: [],
      selectedStoreList: {
        ids: [],
        names: []
      },
      graphData: [],
      dataConfig: {
        scoreType: 'nps',
        scoreTypeMonths: 3,
        blnCurrentMonth: true
      },
      reportRan: false,
      showLoading: false,
      showGraph: false
    };
  },
  created() {
    this.getStoreList()
  },
  methods: {
    filterHandler: function(type, val) {
      this.dataConfig[type] = val

      if(this.reportRan) {
        this.onRunReport()
      }
    },
    checkboxHandler: function() {
      this.dataConfig.blnCurrentMonth = !this.dataConfig.blnCurrentMonth

      if(this.reportRan) {
        this.onRunReport()
      }
    },
    removeSelectedStore: function(storeName) {
      const index = this.selectedStoreList.names.indexOf(storeName)
      this.selectedStoreList.ids.splice(index, 1)
      this.selectedStoreList.names.splice(index, 1)
    },
    setSelected(row) {
      
      if (['all', 'none'].includes(row)) {
        this.selectedStoreList = {
          ids: [],
          names: []
        }
        if(row == 'none') return

        for(let i = 0; i < this.storeList.length && i < 10; i++) {
          this.selectedStoreList.ids.push(this.storeList[i].id)
          this.selectedStoreList.names.push(this.storeList[i].name)
        }
      } else {
        // The graph array is returned in id sorted order. In order to make
        // it less confusing, the list of stores should ordered the same

        const currentArray = this.selectedStoreList.ids
        const currentLength = currentArray.length

        if (currentArray.includes(row.id)) {
          this.removeSelectedStore(row.name)
        } else {
          if(currentLength >= 10) return

          let index = currentArray.findIndex(el => el > row.id)
          if(index < 0) index = currentArray.length
          this.selectedStoreList.ids.splice(index, 0, row.id)
          this.selectedStoreList.names.splice(index, 0, row.name)
        }
      }
      
    },
    onStoreHover(row, action) {
      const ele = this.$refs[row.id];

      if(action == 'enter') {
        ele[0].classList.add("bg-info");
      } else if (action == 'leave' && !this.selectedStoreList.ids.includes(row.id)) {
        ele[0].classList.remove("bg-info");
      }
    },
    async onRunReport() {
      if(this.selectedStoreList.ids.length == 0) return

      this.showLoading = true
      this.showGraph = true

      const { dataConfig } = this
      const options = {
        params: {
          storeId: this.selectedStoreList.ids,
          scoreType: dataConfig.scoreType,
          scoreTypeMonths: dataConfig.scoreTypeMonths,
          blnCurrentMonth: dataConfig.blnCurrentMonth
        }
      }

      this.graphData = await feedbackAPI.GETScoresGraph(options)

      this.showLoading = false
      this.reportRan = true
    },
    getStoreList: async function() {
      this.storeList = await feedbackAPI.GETStoreList()
    }
  }
};
</script>